// Decorative scientific background art. Pure SVG + CSS, aria-hidden, pointer-events: none.
// Low-opacity strokes only — never competes with content. Animations are CSS-only and are
// globally disabled under prefers-reduced-motion (see index.css), so no JS lifecycle is involved.
//
// Two usage modes:
//   <SciBackground variant="hero" />                       — original universal cyan/teal variants
//   <CategoryBackground variant="pareto" accent="warn" accent2="critical" /> — per-category accent
import { VARIANT_SURFACE, surfaceGradient } from "../categorySurfaces";

const CY = "rgba(32,184,205,0.13)";
const CY2 = "rgba(32,184,205,0.22)";
const TE = "rgba(19,124,139,0.18)";
const ST = "rgba(138,153,168,0.14)";

// Category accent base hues (match tailwind.config.js tokens)
const HUE = {
  cyan: "32,184,205",
  teal: "19,124,139",
  eblue: "45,127,249",
  violet: "124,111,229",
  success: "53,183,121",
  warn: "233,162,59",
  critical: "216,88,88",
  steel: "138,153,168",
};

// Build a palette from two accent keys: a1/a2/aHi = primary, b1/b2 = secondary.
function palette(accent, accent2) {
  const A = HUE[accent] || HUE.cyan;
  const B = HUE[accent2] || HUE.teal;
  return {
    a1: `rgba(${A},0.18)`,
    a2: `rgba(${A},0.32)`,
    aHi: `rgba(${A},0.68)`,
    b1: `rgba(${B},0.21)`,
    b2: `rgba(${B},0.32)`,
    // ambient surface wash + divider tones (drive the section's color identity)
    aWash: `rgba(${A},0.16)`,
    bWash: `rgba(${B},0.14)`,
    aLine: `rgba(${A},0.60)`,
    bLine: `rgba(${B},0.55)`,
    steel: ST,
  };
}

// ---------- shared base motif primitives (universal cyan/teal) ----------

function helixPaths() {
  const a = [];
  const b = [];
  for (let y = 0; y <= 620; y += 8) {
    a.push(`${60 + 34 * Math.sin(y / 46)},${y}`);
    b.push(`${60 + 34 * Math.sin(y / 46 + Math.PI)},${y}`);
  }
  const rungs = [];
  for (let y = 12; y <= 612; y += 44) {
    rungs.push([60 + 34 * Math.sin(y / 46), y, 60 + 34 * Math.sin(y / 46 + Math.PI), y]);
  }
  return { a: a.join(" "), b: b.join(" "), rungs };
}
const HELIX = helixPaths();

const Helix = ({ className = "" }) => (
  <svg className={className} width="120" height="620" viewBox="0 0 120 620" fill="none">
    <polyline points={HELIX.a} stroke={CY2} strokeWidth="1.4" />
    <polyline points={HELIX.b} stroke={CY} strokeWidth="1.4" />
    {HELIX.rungs.map(([x1, y1, x2, y2], i) => (
      <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke={TE} strokeWidth="1" />
    ))}
  </svg>
);

const Ribbon = ({ className = "" }) => (
  <svg className={className} width="340" height="220" viewBox="0 0 340 220" fill="none">
    <path d="M8,180 C30,120 50,120 66,150 C82,180 96,180 108,150 C120,120 136,118 150,140 C164,162 182,164 196,132 C208,104 226,100 244,118" stroke={CY2} strokeWidth="1.6" />
    <path d="M150,140 l18,-6 M168,134 l6,14" stroke={CY2} strokeWidth="1.6" />
    <path d="M244,118 C262,138 280,140 296,120 C310,102 324,104 334,118" stroke={CY} strokeWidth="1.6" />
    <path d="M20,60 l30,-14 M50,46 l30,14 M80,60 l30,-14 M110,46 l30,14 M140,60 l30,-14 M170,46 l30,14" stroke={TE} strokeWidth="1.4" />
    <path d="M200,46 l34,14 l-8,0 M234,60 l-10,-4 M234,60 l-4,10" stroke={TE} strokeWidth="1.4" />
    <circle cx="8" cy="180" r="3" fill="none" stroke={CY} strokeWidth="1.2" />
    <circle cx="334" cy="118" r="3" fill="none" stroke={CY} strokeWidth="1.2" />
  </svg>
);

function tracePoints(w, h, seed) {
  const pts = [];
  const peaks = [
    [0.18, 0.55, 0.02],
    [0.42, 0.3, 0.015],
    [0.58, 0.85, 0.022],
    [0.74, 0.4, 0.03],
    [0.88, 0.62, 0.018],
  ];
  for (let i = 0; i <= 120; i++) {
    const t = i / 120;
    let y = 0.86;
    peaks.forEach(([p, amp, wid]) => {
      y -= amp * Math.exp(-((t - p) ** 2) / (2 * wid * wid));
    });
    y += 0.008 * Math.sin(i * seed);
    pts.push(`${(t * w).toFixed(1)},${(y * h).toFixed(1)}`);
  }
  return pts.join(" ");
}
const TRACE_A = tracePoints(560, 150, 1.3);
const TRACE_B = tracePoints(560, 150, 2.1);

const ElectroTrace = ({ className = "", draw = false, points = TRACE_A, warnPeak = true }) => (
  <svg className={className} width="560" height="150" viewBox="0 0 560 150" fill="none">
    <line x1="0" y1="129" x2="560" y2="129" stroke={TE} strokeWidth="1" strokeDasharray="2 6" />
    <polyline
      points={points}
      stroke={CY2}
      strokeWidth="1.6"
      strokeLinejoin="round"
      strokeLinecap="round"
      {...(draw ? { strokeDasharray: 1400, className: "animate-draw-line" } : {})}
    />
    {warnPeak && <circle cx={0.58 * 560} cy={(0.86 - 0.85) * 150 + 4} r="4" fill="none" stroke="rgba(255,179,71,0.35)" strokeWidth="1.4" />}
  </svg>
);

const NODES = [
  [30, 40], [110, 18], [190, 60], [270, 30], [80, 110], [170, 130], [260, 110], [40, 180], [140, 200], [230, 180], [310, 150], [320, 60],
];
const EDGES = [[0, 1], [1, 2], [2, 3], [0, 4], [1, 4], [2, 5], [3, 5], [4, 5], [4, 7], [5, 8], [5, 9], [6, 10], [3, 11], [6, 9], [7, 8], [8, 9], [2, 6], [9, 10]];

const Network = ({ className = "" }) => (
  <svg className={className} width="340" height="220" viewBox="0 0 340 220" fill="none">
    {EDGES.map(([a, b], i) => (
      <line key={i} x1={NODES[a][0]} y1={NODES[a][1]} x2={NODES[b][0]} y2={NODES[b][1]} stroke={CY} strokeWidth="1" />
    ))}
    {NODES.map(([x, y], i) => (
      <circle key={i} cx={x} cy={y} r={i % 4 === 0 ? 4 : 2.4} fill="none" stroke={i % 5 === 0 ? CY2 : TE} strokeWidth="1.2" className={i % 5 === 0 ? "animate-pulse-soft" : undefined} />
    ))}
  </svg>
);

const Pipeline = ({ className = "" }) => (
  <div className={className}>
    {[0, 1, 2].map((row) => (
      <div key={row} className="relative h-7">
        <div className="absolute inset-x-0 top-1/2 h-px" style={{ background: row === 1 ? CY : TE }} />
        {[0, 1, 2, 3].map((n) => (
          <span key={n} className="absolute top-1/2 -mt-[3px] w-1.5 h-1.5 rounded-full" style={{ left: `${n * 30 + 8}%`, background: CY2 }} />
        ))}
        <span className={`absolute top-1/2 -mt-[2px] w-1 h-1 rounded-full bg-cyan/60 ${row === 1 ? "sci-packet-delay" : "sci-packet"}`} style={{ left: `${8 + row * 30}%` }} />
      </div>
    ))}
  </div>
);

const Distributions = ({ className = "" }) => (
  <svg className={className} width="420" height="160" viewBox="0 0 420 160" fill="none">
    <path d="M10,140 C70,140 90,30 150,30 C210,30 230,140 290,140" stroke={CY2} strokeWidth="1.5" />
    <path d="M90,140 C150,140 170,55 230,55 C290,55 310,140 370,140" stroke={TE} strokeWidth="1.5" />
    <path d="M40,140 C120,140 160,85 220,85 C280,85 320,140 400,140" stroke={CY} strokeWidth="1.2" strokeDasharray="5 5" />
    <line x1="150" y1="18" x2="150" y2="140" stroke={TE} strokeWidth="1" strokeDasharray="2 5" />
    <line x1="230" y1="43" x2="230" y2="140" stroke={TE} strokeWidth="1" strokeDasharray="2 5" />
  </svg>
);

const Telemetry = ({ className = "" }) => {
  const pts = [];
  for (let i = 0; i <= 100; i++) {
    const t = i / 100;
    pts.push(`${(t * 560).toFixed(1)},${(70 + 26 * Math.sin(t * Math.PI * 4) + 6 * Math.sin(t * 40)).toFixed(1)}`);
  }
  return (
    <svg className={className} width="560" height="150" viewBox="0 0 560 150" fill="none">
      <polyline points={pts.join(" ")} stroke={CY2} strokeWidth="1.5" />
      {[0.2, 0.55, 0.9].map((t, i) => (
        <circle key={i} cx={t * 560} cy={70 + 26 * Math.sin(t * Math.PI * 4)} r="4" fill="none" stroke={CY2} strokeWidth="1.4" className="animate-pulse-soft" />
      ))}
    </svg>
  );
};

const DocLayers = ({ className = "" }) => (
  <svg className={className} width="340" height="230" viewBox="0 0 340 230" fill="none">
    <rect x="70" y="30" width="150" height="190" rx="3" stroke={TE} strokeWidth="1.3" />
    <rect x="55" y="20" width="150" height="190" rx="3" stroke={CY} strokeWidth="1.3" />
    <rect x="40" y="10" width="150" height="190" rx="3" stroke={CY2} strokeWidth="1.5" />
    {[44, 62, 80, 98].map((y) => (
      <line key={y} x1="56" y1={y} x2={150} y2={y} stroke={TE} strokeWidth="1" />
    ))}
    <path d="M190,60 C240,60 240,120 290,120" stroke={CY2} strokeWidth="1.3" />
    <path d="M190,140 C240,140 240,180 290,180" stroke={CY} strokeWidth="1.3" />
    <circle cx="290" cy="120" r="5" fill="none" stroke={CY2} strokeWidth="1.4" />
    <circle cx="290" cy="180" r="5" fill="none" stroke={TE} strokeWidth="1.4" />
    <circle cx="240" cy="90" r="3" fill="none" stroke={TE} strokeWidth="1.2" className="animate-pulse-soft" />
  </svg>
);

const InstrumentGrid = ({ className = "" }) => (
  <svg className={className} width="560" height="180" viewBox="0 0 560 180" fill="none">
    {[30, 60, 90, 120, 150].map((y) => (
      <line key={y} x1="0" y1={y} x2="560" y2={y} stroke={TE} strokeWidth="1" />
    ))}
    {Array.from({ length: 29 }, (_, i) => i * 20).map((x) => (
      <line key={x} x1={x} y1="150" x2={x} y2="158" stroke={CY} strokeWidth="1" />
    ))}
    <polyline points={TRACE_B} stroke={CY2} strokeWidth="1.5" transform="translate(0,15)" />
  </svg>
);

// ---------- category-specific motif primitives (accent-aware) ----------

// Helper: SVG arc path (used by the PDCA loop)
function describeArc(cx, cy, r, startAngle, endAngle) {
  const s = ((startAngle - 90) * Math.PI) / 180;
  const e = ((endAngle - 90) * Math.PI) / 180;
  const x1 = cx + r * Math.cos(s);
  const y1 = cy + r * Math.sin(s);
  const x2 = cx + r * Math.cos(e);
  const y2 = cy + r * Math.sin(e);
  return `M ${x1} ${y1} A ${r} ${r} 0 0 1 ${x2} ${y2}`;
}

// Blueprint engineering grid with pulsing connection paths (Projects)
const BlueprintMotif = ({ p }) => (
  <svg width="360" height="230" viewBox="0 0 360 230" fill="none">
    {Array.from({ length: 6 }, (_, i) => (
      <line key={"h" + i} x1="0" y1={20 + i * 42} x2="360" y2={20 + i * 42} stroke={p.b1} strokeWidth="0.8" />
    ))}
    {Array.from({ length: 9 }, (_, i) => (
      <line key={"v" + i} x1={20 + i * 42} y1="0" x2={20 + i * 42} y2="230" stroke={p.b1} strokeWidth="0.8" />
    ))}
    <path d="M40,180 L120,120 L200,150 L300,60" stroke={p.a2} strokeWidth="1.5" strokeDasharray="6 6" className="sci-dashflow" />
    <path d="M40,60 L120,120 L200,80 L300,150" stroke={p.a1} strokeWidth="1.2" strokeDasharray="6 6" className="sci-dashflow-slow" />
    {[[40, 180], [120, 120], [200, 150], [300, 60], [40, 60], [200, 80], [300, 150]].map(([x, y], i) => (
      <circle key={i} cx={x} cy={y} r={i % 3 === 0 ? 4.5 : 3} fill="none" stroke={i % 3 === 0 ? p.a2 : p.b2} strokeWidth="1.3" className={i % 3 === 0 ? "animate-pulse-soft" : undefined} />
    ))}
  </svg>
);

// Analytical peaks + acceptance band + control limits + lot genealogy (Product Quality)
const ProductQualityMotif = ({ p }) => (
  <svg width="400" height="230" viewBox="0 0 400 230" fill="none">
    <rect x="0" y="70" width="400" height="44" fill={p.b1} opacity="0.35" />
    <line x1="0" y1="70" x2="400" y2="70" stroke={p.b2} strokeWidth="1" strokeDasharray="4 4" />
    <line x1="0" y1="114" x2="400" y2="114" stroke={p.b2} strokeWidth="1" strokeDasharray="4 4" />
    <polyline points={TRACE_A} stroke={p.a2} strokeWidth="1.7" strokeLinejoin="round" transform="translate(0,50) scale(0.714,0.9)" fill="none" />
    {[110, 200, 290].map((x, i) => (
      <line key={i} x1={x} y1="60" x2={x} y2="125" stroke={p.b2} strokeWidth="0.9" strokeDasharray="2 4" />
    ))}
    <path d="M40,190 L130,178 L220,196 L330,168" stroke={p.a1} strokeWidth="1.2" strokeDasharray="5 5" className="sci-dashflow" fill="none" />
    {[[40, 190], [130, 178], [220, 196], [330, 168]].map(([x, y], i) => (
      <circle key={i} cx={x} cy={y} r={i === 3 ? 5 : 3.5} fill="none" stroke={i === 3 ? p.aHi : p.b2} strokeWidth="1.3" className={i === 3 ? "animate-pulse-soft" : undefined} />
    ))}
  </svg>
);

// Fishbone spine + ribs with a pulsing candidate-cause, plus Pareto bars (RCA)
const FishboneMotif = ({ p }) => (
  <svg width="400" height="220" viewBox="0 0 400 220" fill="none">
    <line x1="20" y1="115" x2="330" y2="115" stroke={p.a2} strokeWidth="1.6" />
    <rect x="330" y="100" width="58" height="30" rx="3" stroke={p.aHi} strokeWidth="1.4" />
    {[[70, 1], [130, 1], [190, 1], [250, 1], [100, -1], [160, -1], [220, -1]].map(([x, dir], i) => (
      <line key={i} x1={x} y1="115" x2={x + 42} y2={dir === 1 ? 58 : 172} stroke={i % 2 ? p.b2 : p.a1} strokeWidth="1.2" />
    ))}
    {[[70, 115], [130, 115], [190, 115], [250, 115], [142, 86], [202, 144]].map(([x, y], i) => (
      <circle key={i} cx={x} cy={y} r="3.4" fill="none" stroke={i === 2 ? p.aHi : p.b2} strokeWidth="1.3" className={i === 2 ? "animate-pulse-soft" : undefined} />
    ))}
    {[54, 40, 28, 18, 10].map((h, i) => (
      <rect key={i} x={20 + i * 18} y={215 - h} width="11" height={h} fill={i === 0 ? p.aHi : p.b1} opacity={i === 0 ? 0.7 : 0.6} />
    ))}
    <line x1="14" y1="215" x2="120" y2="215" stroke={p.b2} strokeWidth="1" />
  </svg>
);

// PDCA loop + improving trend + before/after distributions + success markers (Continuous Improvement)
const PdcaMotif = ({ p }) => (
  <svg width="380" height="220" viewBox="0 0 380 220" fill="none">
    <circle cx="92" cy="104" r="56" fill="none" stroke={p.b1} strokeWidth="1.1" />
    {[0, 1, 2, 3].map((i) => (
      <path key={i} d={describeArc(92, 104, 56, i * 90 + 4, i * 90 + 82)} stroke={i === 3 ? p.aHi : p.a2} strokeWidth="1.7" fill="none" className={i === 3 ? "animate-pulse-soft" : undefined} />
    ))}
    {[[92, 40], [156, 104], [92, 168], [28, 104]].map(([x, y], i) => (
      <circle key={i} cx={x} cy={y} r="3.2" fill="none" stroke={p.b2} strokeWidth="1.2" />
    ))}
    <path d="M210,150 C225,150 232,90 250,90 C268,90 275,150 290,150" stroke={p.b2} strokeWidth="1.4" fill="none" />
    <path d="M250,150 C262,150 268,112 282,112 C296,112 302,150 314,150" stroke={p.a2} strokeWidth="1.6" fill="none" />
    <polyline points="200,185 240,178 280,170 320,158 356,146" stroke={p.aHi} strokeWidth="1.7" strokeLinecap="round" className="sci-dashflow" strokeDasharray="5 5" fill="none" />
    {[[240, 178], [320, 158], [356, 146]].map(([x, y], i) => (
      <circle key={i} cx={x} cy={y} r="3" fill="none" stroke={p.aHi} strokeWidth="1.2" className="animate-pulse-soft" />
    ))}
  </svg>
);

// Source → validate → transform → decision pipeline with checkpoints (Automation)
const AutomationMotif = ({ p }) => (
  <svg width="400" height="200" viewBox="0 0 400 200" fill="none">
    {[48, 104, 160].map((y, row) => (
      <g key={row}>
        <line x1="16" y1={y} x2="384" y2={y} stroke={row === 1 ? p.a1 : p.b1} strokeWidth="1.1" />
        {[70, 180, 290].map((x, i) => (
          <circle key={i} cx={x} cy={y} r={i === 1 ? 4.5 : 3.2} fill="none" stroke={i === 1 ? p.a2 : p.b2} strokeWidth="1.2" className={i === 1 && row === 1 ? "animate-pulse-soft" : undefined} />
        ))}
        <rect x="332" y={y - 6} width="12" height="12" rx="2" fill="none" stroke={p.a2} strokeWidth="1.2" />
        <circle r="3" fill={p.aHi} className={row === 1 ? "sci-dot-x-delay" : "sci-dot-x"} style={{ offsetPath: `path('M 16 ${y} L 384 ${y}')` }} />
      </g>
    ))}
  </svg>
);

// Sensor mesh + telemetry trace with gap / recovery markers (IoT)
const IotMotif = ({ p }) => {
  const pts = [];
  for (let i = 0; i <= 100; i++) {
    const t = i / 100;
    pts.push(`${(t * 360).toFixed(1)},${(120 + 22 * Math.sin(t * Math.PI * 4) + 5 * Math.sin(t * 40)).toFixed(1)}`);
  }
  const mesh = [[30, 30], [90, 20], [150, 34], [60, 62], [120, 66], [180, 58]];
  return (
    <svg width="380" height="220" viewBox="0 0 380 220" fill="none">
      {[[0, 1], [1, 2], [0, 3], [1, 4], [2, 5], [3, 4], [4, 5]].map(([a, b], i) => (
        <line key={i} x1={mesh[a][0]} y1={mesh[a][1]} x2={mesh[b][0]} y2={mesh[b][1]} stroke={p.b1} strokeWidth="1" />
      ))}
      {mesh.map(([x, y], i) => (
        <circle key={i} cx={x} cy={y} r={i % 2 ? 3 : 4} fill="none" stroke={i % 2 ? p.a2 : p.b2} strokeWidth="1.2" className={i % 2 === 0 ? "animate-pulse-soft" : undefined} />
      ))}
      <polyline points={pts.join(" ")} stroke={p.a2} strokeWidth="1.6" transform="translate(10,40)" fill="none" />
      {[40, 120, 200, 280, 340].map((x, i) => (
        <line key={i} x1={x} y1="188" x2={x} y2="196" stroke={p.b2} strokeWidth="1" />
      ))}
      <rect x="196" y="120" width="26" height="56" fill="none" stroke={p.b2} strokeWidth="1" strokeDasharray="3 3" />
      <circle cx="252" cy="132" r="4.5" fill="none" stroke={p.aHi} strokeWidth="1.3" className="animate-pulse-soft" />
    </svg>
  );
};

// Scatter cluster + distribution curve + heatmap grid + control line (Analytics)
const AnalyticsMotif = ({ p }) => (
  <svg width="400" height="220" viewBox="0 0 400 220" fill="none">
    {Array.from({ length: 4 }, (_, r) =>
      Array.from({ length: 6 }, (_, c) => (
        <rect key={r + "-" + c} x={240 + c * 24} y={20 + r * 24} width="20" height="20" fill={(r + c) % 3 === 0 ? p.a1 : p.b1} opacity={(r + c) % 3 === 0 ? 0.8 : 0.45} />
      ))
    )}
    <path d="M20,170 C60,170 75,60 120,60 C165,60 180,170 220,170" stroke={p.a2} strokeWidth="1.6" fill="none" />
    {[[45, 120], [70, 95], [95, 130], [120, 70], [150, 105], [175, 88], [95, 60], [140, 140]].map(([x, y], i) => (
      <circle key={i} cx={x} cy={y} r={i % 4 === 0 ? 3.4 : 2.4} fill={i % 4 === 0 ? p.aHi : p.b2} opacity="0.7" className={i % 4 === 0 ? "animate-pulse-soft" : undefined} />
    ))}
    <line x1="20" y1="196" x2="220" y2="196" stroke={p.b2} strokeWidth="1" />
    <line x1="20" y1="184" x2="220" y2="184" stroke={p.a1} strokeWidth="0.9" strokeDasharray="3 4" />
    <polyline points="20,192 60,188 100,194 140,186 180,192 220,187" stroke={p.a2} strokeWidth="1.3" fill="none" />
  </svg>
);

// Document lifecycle: draft → review → approval → training → effective → superseded (QMS)
const QmsMotif = ({ p }) => {
  const stages = [30, 90, 150, 210, 270, 330];
  return (
    <svg width="380" height="220" viewBox="0 0 380 220" fill="none">
      <rect x="24" y="20" width="92" height="74" rx="3" stroke={p.b2} strokeWidth="1.2" />
      <rect x="32" y="28" width="92" height="74" rx="3" stroke={p.a1} strokeWidth="1.2" />
      <rect x="40" y="36" width="92" height="74" rx="3" stroke={p.a2} strokeWidth="1.5" />
      {[52, 66, 80, 94].map((y) => (
        <line key={y} x1="52" y1={y} x2="116" y2={y} stroke={p.b1} strokeWidth="1" />
      ))}
      <polyline points={stages.map((x, i) => `${x},${i % 2 ? 176 : 162}`).join(" ")} stroke={p.a1} strokeWidth="1.2" strokeDasharray="5 5" className="sci-dashflow" fill="none" />
      {stages.map((x, i) => (
        <g key={i}>
          <circle cx={x} cy={i % 2 ? 176 : 162} r={i === stages.length - 1 ? 3 : 5} fill="none" stroke={i === stages.length - 1 ? p.b2 : p.a2} strokeWidth="1.3" strokeDasharray={i === stages.length - 1 ? "2 3" : undefined} />
          {i < stages.length - 1 && <circle cx={x} cy={i % 2 ? 176 : 162} r="2" fill={p.aHi} className="animate-pulse-soft" />}
        </g>
      ))}
      <path d="M150,60 L210,60 M210,60 l-8,-5 M210,60 l-8,5" stroke={p.a2} strokeWidth="1.3" />
    </svg>
  );
};

// Skill lattice / hex capability matrix (Expertise)
const LatticeMotif = ({ p }) => {
  const hex = (cx, cy, r) =>
    Array.from({ length: 6 }, (_, i) => {
      const a = (Math.PI / 3) * i - Math.PI / 6;
      return `${(cx + r * Math.cos(a)).toFixed(1)},${(cy + r * Math.sin(a)).toFixed(1)}`;
    }).join(" ");
  const cells = [[70, 60], [140, 100], [70, 140], [210, 60], [210, 140], [280, 100]];
  return (
    <svg width="360" height="200" viewBox="0 0 360 200" fill="none">
      {[[0, 1], [1, 3], [0, 2], [2, 4], [3, 5], [4, 5], [1, 4]].map(([a, b], i) => (
        <line key={i} x1={cells[a][0]} y1={cells[a][1]} x2={cells[b][0]} y2={cells[b][1]} stroke={p.b1} strokeWidth="1" strokeDasharray="4 5" className={i % 2 ? "sci-dashflow" : undefined} />
      ))}
      {cells.map(([x, y], i) => (
        <polygon key={i} points={hex(x, y, i % 2 ? 20 : 26)} fill="none" stroke={i % 2 ? p.a2 : p.b2} strokeWidth="1.3" className={i % 3 === 0 ? "animate-pulse-soft" : undefined} />
      ))}
    </svg>
  );
};

// Precision-manufacturing grid + ghosted protein ribbon (About)
const AboutMotif = ({ p }) => (
  <svg width="380" height="220" viewBox="0 0 380 220" fill="none">
    {Array.from({ length: 6 }, (_, i) => (
      <line key={"h" + i} x1="0" y1={20 + i * 40} x2="380" y2={20 + i * 40} stroke={p.b1} strokeWidth="0.8" />
    ))}
    {Array.from({ length: 10 }, (_, i) => (
      <line key={"v" + i} x1={20 + i * 40} y1="0" x2={20 + i * 40} y2="220" stroke={p.b1} strokeWidth="0.8" />
    ))}
    <path d="M20,170 C50,120 70,120 90,150 C110,180 124,180 140,150 C156,120 172,118 190,140 C208,162 226,164 244,132" stroke={p.a1} strokeWidth="1.5" fill="none" className="sci-drift" />
    {[[90, 150], [190, 140], [244, 132]].map(([x, y], i) => (
      <circle key={i} cx={x} cy={y} r="3" fill="none" stroke={p.a2} strokeWidth="1.2" className="animate-pulse-soft" />
    ))}
  </svg>
);

// ---------- universal (original) variants — unchanged ----------
const parallax = (fx, fy, extra = "") => ({
  transform: `${extra} translate3d(calc(var(--px, 0) * ${fx}px), calc(var(--py, 0) * ${fy}px), 0)`,
  transition: "transform 0.9s ease-out",
});

const VARIANTS = {
  hero: (
    <>
      <div className="absolute left-0 top-0 h-full hidden lg:flex items-center opacity-70" style={parallax(8, 5)}>
        <div className="sci-drift-slow"><Helix /></div>
      </div>
      <div className="absolute left-24 bottom-6 hidden xl:block opacity-60" style={parallax(-6, 4)}>
        <div className="sci-drift"><Ribbon /></div>
      </div>
      <div className="absolute right-0 top-8 hidden md:block opacity-70" style={parallax(5, -4)}><Network /></div>
      <div className="absolute left-1/2 bottom-2 hidden md:block opacity-60" style={parallax(3, 2, "translateX(-50%)")}><ElectroTrace draw /></div>
    </>
  ),
  signals: (
    <>
      <div className="absolute right-4 top-6 hidden md:block opacity-60"><ElectroTrace /></div>
      <div className="absolute left-2 bottom-8 hidden lg:block opacity-50"><Network /></div>
    </>
  ),
  process: (
    <>
      <div className="absolute right-6 top-8 hidden md:block opacity-60"><Distributions /></div>
      <div className="absolute left-0 bottom-10 w-72 hidden lg:block opacity-70"><Pipeline /></div>
    </>
  ),
  network: (
    <>
      <div className="absolute right-2 top-10 hidden md:block opacity-60"><Network /></div>
      <div className="absolute left-4 bottom-6 w-80 hidden lg:block opacity-70"><Pipeline /></div>
    </>
  ),
  telemetry: (
    <>
      <div className="absolute right-0 top-6 hidden md:block opacity-60"><Telemetry /></div>
      <div className="absolute left-6 bottom-6 hidden lg:block opacity-50"><Helix /></div>
    </>
  ),
  instrument: (
    <>
      <div className="absolute right-4 top-4 hidden md:block opacity-60"><InstrumentGrid /></div>
      <div className="absolute left-0 bottom-4 hidden lg:block opacity-50"><Distributions /></div>
    </>
  ),
  docs: (
    <>
      <div className="absolute right-6 top-6 hidden md:block opacity-70 sci-drift"><DocLayers /></div>
      <div className="absolute left-4 bottom-8 hidden lg:block opacity-60"><Ribbon /></div>
    </>
  ),
};

export default function SciBackground({ variant = "signals" }) {
  return (
    <div aria-hidden="true" className="absolute inset-0 overflow-hidden pointer-events-none select-none" data-testid={`live-bg-${variant}`}>
      {VARIANTS[variant] || null}
    </div>
  );
}

// ---------- accent-aware category backgrounds ----------
const CATEGORY_VARIANTS = {
  blueprint: (p) => (
    <>
      <div className="absolute right-0 top-10 hidden md:block opacity-60"><BlueprintMotif p={p} /></div>
      <div className="absolute left-4 bottom-8 hidden lg:block opacity-45"><BlueprintMotif p={p} /></div>
    </>
  ),
  quality: (p) => (
    <>
      <div className="absolute right-2 top-8 hidden md:block opacity-60"><ProductQualityMotif p={p} /></div>
      <div className="absolute left-2 bottom-6 hidden lg:block opacity-45"><ProductQualityMotif p={p} /></div>
    </>
  ),
  pareto: (p) => (
    <>
      <div className="absolute right-2 top-8 hidden md:block opacity-60"><FishboneMotif p={p} /></div>
      <div className="absolute left-4 bottom-10 hidden lg:block opacity-45"><FishboneMotif p={p} /></div>
    </>
  ),
  pdca: (p) => (
    <>
      <div className="absolute right-4 top-8 hidden md:block opacity-60"><PdcaMotif p={p} /></div>
      <div className="absolute left-4 bottom-8 hidden lg:block opacity-45"><PdcaMotif p={p} /></div>
    </>
  ),
  pipeline: (p) => (
    <>
      <div className="absolute right-0 top-12 hidden md:block opacity-60"><AutomationMotif p={p} /></div>
      <div className="absolute left-2 bottom-10 hidden lg:block opacity-50"><AutomationMotif p={p} /></div>
    </>
  ),
  mesh: (p) => (
    <>
      <div className="absolute right-0 top-8 hidden md:block opacity-60"><IotMotif p={p} /></div>
      <div className="absolute left-4 bottom-8 hidden lg:block opacity-45"><IotMotif p={p} /></div>
    </>
  ),
  scatter: (p) => (
    <>
      <div className="absolute right-2 top-8 hidden md:block opacity-60"><AnalyticsMotif p={p} /></div>
      <div className="absolute left-2 bottom-8 hidden lg:block opacity-45"><AnalyticsMotif p={p} /></div>
    </>
  ),
  lifecycle: (p) => (
    <>
      <div className="absolute right-4 top-6 hidden md:block opacity-60 sci-drift"><QmsMotif p={p} /></div>
      <div className="absolute left-4 bottom-8 hidden lg:block opacity-45"><QmsMotif p={p} /></div>
    </>
  ),
  lattice: (p) => (
    <>
      <div className="absolute right-4 top-10 hidden md:block opacity-60"><LatticeMotif p={p} /></div>
      <div className="absolute left-6 bottom-8 hidden lg:block opacity-45"><LatticeMotif p={p} /></div>
    </>
  ),
  precision: (p) => (
    <>
      <div className="absolute right-0 top-8 hidden md:block opacity-55"><AboutMotif p={p} /></div>
      <div className="absolute left-4 bottom-8 hidden lg:block opacity-45"><AboutMotif p={p} /></div>
    </>
  ),
};

export function CategoryBackground({ variant, accent = "cyan", accent2 = "teal" }) {
  const render = CATEGORY_VARIANTS[variant]; // undefined for intro/closing sections (base+glow only)
  const p = palette(accent, accent2);
  const flowKey = VARIANT_SURFACE[variant] || "overview";
  return (
    <div aria-hidden="true" className="absolute inset-0 overflow-hidden pointer-events-none select-none" data-testid={`live-bg-${variant}`}>
      {/* LAYER 1 — base chromatic gradient (continuous flow; top blends from previous section) */}
      <div className="absolute inset-0" style={{ background: surfaceGradient(flowKey) }} />
      {/* LAYER 2 — large soft ambient glows in the category accents */}
      <div
        className="absolute inset-0"
        style={{ background: `radial-gradient(52% 42% at 86% 6%, ${p.aWash}, transparent 64%), radial-gradient(46% 42% at 6% 94%, ${p.bWash}, transparent 64%)` }}
      />
      {/* LAYER 3 — category scientific motif (smooth edge blending comes from LAYER 1) */}
      {render ? render(p) : null}
    </div>
  );
}
